/**
 * Capitalization correction for CapCut subtitle templates.
 *
 * CapCut applies a visual "capital: upper" styling that bakes uppercase into
 * all text fields (recognize_text, content.text, words.text). This module
 * provides heuristics to revert artificial capitalization while preserving
 * proper nouns.
 *
 * Algorithm:
 * 1. Detect proper nouns: Title Case words NOT in the Spanish common words dictionary
 * 2. Lowercase entire text
 * 3. Restore detected proper nouns
 */

/**
 * Spanish common words dictionary (~500 entries).
 * Words in this set are considered common vocabulary and will be lowercased.
 * Words NOT in this set that appear with Title Case are treated as proper nouns.
 */
const SPANISH_COMMON_WORDS: ReadonlySet<string> = new Set([
  // ─── Artículos y determinantes ──────────────────────────────────────────
  "la", "el", "los", "las", "un", "una", "unos", "unas",
  "lo", "al", "del",
  // ─── Demostrativos ─────────────────────────────────────────────────────
  "ese", "esa", "esos", "esas", "este", "esta", "estos", "estas",
  "esto", "eso", "aquel", "aquella", "aquellos", "aquellas",
  // ─── Posesivos ─────────────────────────────────────────────────────────
  "mi", "mis", "tu", "tus", "su", "sus", "nuestro", "nuestra",
  "nuestros", "nuestras", "vuestro", "vuestra",
  // ─── Pronombres personales y átonos ────────────────────────────────────
  "yo", "me", "te", "se", "le", "les", "nos", "os",
  "él", "ella", "ellos", "ellas", "usted", "ustedes",
  "mí", "ti", "sí", "consigo", "conmigo", "contigo",
  // ─── Preposiciones ─────────────────────────────────────────────────────
  "de", "en", "con", "por", "para", "sin", "sobre", "entre",
  "hasta", "desde", "hacia", "durante", "mediante", "según",
  "tras", "contra", "ante", "bajo",
  // ─── Conjunciones ──────────────────────────────────────────────────────
  "y", "e", "o", "u", "ni", "que", "como", "pero", "sino",
  "porque", "aunque", "cuando", "donde", "mientras", "pues",
  "si", "cual", "quien", "cuyo", "cuya",
  // ─── Adverbios comunes ─────────────────────────────────────────────────
  "no", "sí", "ya", "muy", "más", "menos", "bien", "mal",
  "también", "tampoco", "aquí", "ahí", "allí", "así",
  "siempre", "nunca", "después", "antes", "ahora", "hoy",
  "entonces", "luego", "pronto", "tarde", "solo", "apenas",
  "además", "quizás", "acaso", "aún", "todavía", "bastante",
  // ─── Verbos auxiliares y muy comunes (infinitivos) ─────────────────────
  "ser", "estar", "haber", "tener", "hacer", "poder", "decir",
  "ir", "ver", "dar", "saber", "querer", "llegar", "pasar",
  "deber", "poner", "parecer", "quedar", "creer", "hablar",
  "llevar", "dejar", "seguir", "encontrar", "llamar", "venir",
  "pensar", "salir", "volver", "tomar", "conocer", "vivir",
  "sentir", "recordar", "tratar", "mirar", "contar", "empezar",
  "esperar", "buscar", "existir", "entrar", "trabajar", "escribir",
  "perder", "producir", "ocurrir", "acabar", "cumplir",
  "permitir", "aparecer", "conseguir", "comenzar", "servir",
  "sacar", "necesitar", "mantener", "resultar", "leer",
  "caer", "cambiar", "mostrar", "crear", "abrir", "considerar",
  "oír", "terminar", "jugar", "mover", "pedir", "recibir",
  "correr", "dormir", "morir", "nacer", "caminar", "cantar",
  "bailar", "reír", "llorar", "gritar", "saltar", "respirar",
  "abrazar", "explicar", "intentar", "aprender", "enseñar",
  "guardar", "ayudar", "comprender", "tocar", "cerrar",
  "calmar", "explotar", "transformar", "desaparecer", "brincar",
  "patear", "golpear", "discutir", "imaginar", "inflar",
  // ─── Verbos conjugados frecuentes ──────────────────────────────────────
  "es", "era", "fue", "son", "está", "hay", "tiene", "hace",
  "puede", "va", "dice", "dijo", "habló", "continuó", "preguntó",
  "respondió", "veo", "vio", "dio", "sabe", "quiere", "sale",
  "viene", "puso", "tuvo", "hizo", "daba", "tenía", "había",
  "hacía", "podía", "quería", "sabía", "decía", "vivía",
  "sentía", "recordaba", "recordará", "movió", "sintió",
  "ocurrió", "discutió", "comprendió", "aprendió", "transformó",
  "desapareció", "terminó", "cerró", "guardaba", "imaginó",
  "molestó", "envolvió", "llenándolo", "sintiéndose",
  // ─── Adjetivos comunes ─────────────────────────────────────────────────
  "gran", "grande", "malo", "mala", "bueno", "buena",
  "nuevo", "nueva", "todo", "toda", "todos", "todas",
  "mucho", "mucha", "muchos", "muchas", "poco", "poca",
  "otro", "otra", "otros", "otras", "mismo", "misma",
  "último", "última", "primero", "primera", "largo", "larga",
  "mejor", "peor", "mayor", "menor", "alto", "alta",
  "bajo", "baja", "viejo", "vieja", "joven", "pequeño", "pequeña",
  "negro", "negra", "blanco", "blanca", "rojo", "roja",
  "fuerte", "profundo", "profunda", "seguro", "segura",
  "mágico", "mágica", "feliz", "difícil", "fácil",
  "importante", "posible", "necesario", "diferente", "siguiente",
  "invisible", "cálido", "cálida", "tranquilo", "tranquila",
  "sorprendente", "mojado", "perdido",
  // ─── Sustantivos comunes (narrativa, naturaleza, cuerpo, lugar) ────────
  "día", "días", "noche", "vez", "veces", "tiempo", "momento",
  "año", "años", "vida", "cosa", "cosas", "mundo", "parte",
  "forma", "lugar", "lado", "punto", "casa", "agua",
  "tierra", "piedra", "mar", "sol", "fuego", "aire",
  "luz", "nombre", "palabra", "historia", "camino", "fin",
  "cabeza", "cara", "ojos", "ojo", "mano", "manos",
  "cuerpo", "pie", "pies", "voz", "corazón", "brazo",
  "brazos", "espalda", "hombro", "dedo",
  "niño", "niña", "hombre", "mujer", "hijo", "hija",
  "padre", "madre", "hermano", "hermana", "amigo", "amiga",
  "familia", "gente", "persona", "personas",
  "escuela", "clase", "fila", "puerta", "ventana", "mesa",
  "calle", "ciudad", "país", "pueblo", "cuarto", "habitación",
  "tortuga", "volcán", "lección", "enojo", "abrazo", "calma",
  "pecera", "charco", "torre", "bloque", "bloques", "osito",
  "peluche", "juguete", "juguetes",
  "paz", "humo", "globo", "cajita", "risa", "rabia",
  "alegría", "frustración", "emoción", "ocasión", "práctica",
  "tomate", "ceño", "piso", "suelo", "manto",
  "tranquilidad", "fuerza", "esfuerzo",
  // ─── Números y cantidades ──────────────────────────────────────────────
  "uno", "dos", "tres", "cuatro", "cinco", "diez", "cien", "mil",
  // ─── Palabras funcionales adicionales ──────────────────────────────────
  "cada", "algo", "alguien", "nada", "nadie", "algún", "alguno",
  "alguna", "ningún", "ninguno", "ninguna", "tanto", "tanta",
  "tantos", "demás", "ambos", "varios", "varias",
  "dentro", "fuera", "arriba", "abajo", "cerca", "lejos",
  "adelante", "atrás", "encima", "debajo",
]);

/**
 * Spanish frequency words used to detect if text is likely in Spanish.
 * These are extremely common function words that appear in almost any Spanish text.
 */
const SPANISH_MARKER_WORDS: ReadonlySet<string> = new Set([
  "que", "de", "en", "un", "una", "el", "la", "los", "las",
  "por", "con", "no", "es", "se", "su", "sus", "para", "como",
  "pero", "más", "fue", "ya", "muy", "también", "había",
  "cuando", "donde", "mientras", "porque", "aunque", "sin",
  "del", "al", "lo", "le", "les", "nos", "me", "te",
  "era", "ser", "este", "esta", "ese", "esa", "todo", "toda",
  "otro", "otra", "cada", "entre", "hasta", "sobre", "después",
  "siempre", "nunca", "aquí", "así", "bien", "yo", "él",
]);

const UNICODE_LETTER_PATTERN = /[a-záéíóúñüA-ZÁÉÍÓÚÑÜ]/;

/**
 * Determines if a text is likely Spanish based on word frequency analysis.
 * Returns true if a sufficient proportion of words are common Spanish markers.
 */
export function isLikelySpanish(text: string): boolean {
  const words = text.toLowerCase().split(/\s+/).filter((w) => w.length > 0);
  if (words.length < 3) {
    return false;
  }

  let markerCount = 0;
  for (const word of words) {
    const clean = word.replace(/[^a-záéíóúñü]/g, "");
    if (SPANISH_MARKER_WORDS.has(clean)) {
      markerCount += 1;
    }
  }

  // Require at least 2 marker matches AND a meaningful ratio
  return markerCount >= 2 && markerCount / words.length >= 0.15;
}

/**
 * Detects proper nouns in text by identifying Title Case words
 * that are NOT present in the Spanish common words dictionary.
 */
export function detectProperNouns(text: string): Set<string> {
  const properNouns = new Set<string>();
  const words = text.split(/\s+/);

  for (const word of words) {
    const clean = word.replace(/[^a-záéíóúñüA-ZÁÉÍÓÚÑÜ]/g, "");
    if (clean.length < 2) {
      continue;
    }

    // Title Case: first letter uppercase, rest lowercase
    if (/^[A-ZÁÉÍÓÚÑÜ][a-záéíóúñü]+$/.test(clean)) {
      if (!SPANISH_COMMON_WORDS.has(clean.toLowerCase())) {
        properNouns.add(clean);
      }
    }
  }

  return properNouns;
}

/**
 * Detects if text contains artificial capitalization patterns typically
 * produced by CapCut's capital:"upper" template styling.
 *
 * Looks for:
 * - ALL-CAPS Spanish articles/pronouns (LA, UN, SE, ESA)
 * - Mixed-case words like RECORDARá
 * - Multiple Title Case common words mid-text
 */
export function hasArtificialCapitalization(text: string): boolean {
  // Pattern 1: ALL-CAPS Spanish articles/pronouns
  const allCapsArticles = text.match(/\b(LA|EL|LOS|LAS|UN|UNA|SE|ESA|ESE)\b/g);
  if (allCapsArticles && allCapsArticles.length >= 1) {
    return true;
  }

  // Pattern 2: Mixed case like RECORDARá (uppercase letters followed by lowercase accented)
  if (/[A-ZÁÉÍÓÚÑÜ]{3,}[a-záéíóúñü]/.test(text)) {
    return true;
  }

  // Pattern 3: Multiple Title Case common Spanish words (not at text start)
  const words = text.split(/\s+/);
  let titleCaseCommonCount = 0;
  for (let i = 1; i < words.length; i += 1) {
    const clean = words[i]!.replace(/[^a-záéíóúñüA-ZÁÉÍÓÚÑÜ]/g, "");
    if (/^[A-ZÁÉÍÓÚÑÜ][a-záéíóúñü]+$/.test(clean) && SPANISH_COMMON_WORDS.has(clean.toLowerCase())) {
      titleCaseCommonCount += 1;
    }
  }

  return titleCaseCommonCount >= 1;
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Fixes artificial capitalization applied by CapCut templates.
 *
 * Strategy:
 * 1. Detect proper nouns (Title Case words NOT in Spanish dictionary)
 * 2. Lowercase entire text
 * 3. Restore proper nouns
 * 4. Does NOT re-capitalize sentence starts (matches CapCut's clean ASR output)
 */
export function fixSpanishCapitalization(text: string): string {
  if (!text || !UNICODE_LETTER_PATTERN.test(text)) {
    return text;
  }

  // Step 1: Detect proper nouns before lowercasing
  const properNouns = detectProperNouns(text);

  // Step 2: Lowercase everything
  let result = text.toLowerCase();

  // Step 3: Restore proper nouns
  for (const noun of properNouns) {
    const pattern = new RegExp(`\\b${escapeRegex(noun.toLowerCase())}\\b`, "g");
    result = result.replace(pattern, noun);
  }

  return result;
}
